import React, { useCallback } from 'react';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Tag, Col, Row } from 'antd';

import clsx from 'clsx';
import { formatDate } from 'helpers/date';
import { useFilterTagStore } from 'v2/store/FilterTag';

interface IProps {
  onChange?: any;
  onApply?: any;
  disabled?: boolean;
}

const Tags: React.FC<IProps> = ({ onChange, onApply, disabled }) => {
  const { filter, replaceFilter, applyFilter } = useFilterTagStore();
  const { count, active } = filter;
  const cleanFilter = useCallback(() => {
    const onlyPeriod = filter['period'];

    try {
      replaceFilter({ period: onlyPeriod });

      if (onChange) {
        onChange();
      }

      if (onApply) {
        onApply();
      }
    } catch (error) {
      // silent catch matching original behavior
    }
  }, [replaceFilter, filter, onChange, onApply]);

  const onRemoveTag = useCallback(
    (prop: any, obj: any) => {
      const newFilter = filter;
      let isApplied = false;

      if (Array.isArray(newFilter[prop])) {
        const it = newFilter[prop].find((item: any) => item.label === obj.value);
        isApplied = it.applied;
        newFilter[prop] = newFilter[prop].filter((item: any) => item.label !== obj.value);
      } else {
        isApplied = newFilter[prop] && newFilter[prop].applied ? true : false;
        delete newFilter[prop];
      }

      try {
        replaceFilter(newFilter);

        if (onChange) {
          onChange();
        }

        if (isApplied && onApply) {
          onApply();
        }
      } catch (error) {
        // silent catch matching original behavior
      }
    },
    [replaceFilter, filter, onChange, onApply]
  );

  const handleApply = useCallback(() => {
    try {
      applyFilter();

      if (onApply) {
        onApply();
      }
    } catch (error) {
      // silent catch matching original behavior
    }
  }, [applyFilter, onApply]);

  const getLabels = () => {
    let labels: any = [];

    if (filter) {
      for (const prop in filter) {
        if (Array.isArray(filter[prop])) {
          labels = labels.concat(
            filter[prop].map((item: any) => {
              if (item.label) {
                return {
                  label: prop,
                  value: item.label,
                  prop,
                  nonRemovable: item.nonRemovable,
                  applied: item.applied
                };
              }

              return { label: prop, value: item, prop, nonRemovable: item.nonRemovable, applied: item.applied };
            })
          );
        } else {
          if (prop !== 'count' && prop !== 'active' && prop !== 'modified') {
            if (prop === 'period') {
              const currentPeriod = filter[prop];

              if (currentPeriod) {
                if (currentPeriod.type) {
                  labels.unshift({
                    label: `Período de ${currentPeriod.type.label}`,
                    value: `de ${formatDate(currentPeriod.startDate, 'dd/MM/yyyy')} até ${formatDate(currentPeriod.endDate, 'dd/MM/yyyy')}`,
                    prop,
                    nonRemovable: true,
                    applied: currentPeriod.applied
                  });
                } else {
                  labels.unshift({
                    label: 'Período',
                    value: `de ${formatDate(currentPeriod.startDate, 'dd/MM/yyyy')} até ${formatDate(currentPeriod.endDate, 'dd/MM/yyyy')}`,
                    prop,
                    nonRemovable: true,
                    applied: currentPeriod.applied
                  });
                }
              }
            } else {
              if (filter[prop].label) {
                labels.push({
                  label: prop,
                  value: filter[prop].label,
                  prop,
                  nonRemovable: filter[prop].nonRemovable,
                  applied: filter[prop].applied
                });
              } else {
                labels.push({
                  label: prop,
                  value: filter[prop],
                  prop,
                  nonRemovable: filter[prop].nonRemovable,
                  applied: filter[prop].applied
                });
              }
            }
          }
        }
      }
    }

    if (!labels || !Array.isArray(labels) || !labels.length) {
      return null;
    }

    return labels.map((item: any) => (
      <Tag
        key={item.value}
        closable={!item.nonRemovable && !disabled}
        onClose={() => onRemoveTag(item.prop, item)}
        className={item.applied ? 'bg-link m-1! max-w-full text-white' : 'm-1! max-w-full'}
      >
        {`${item.label}: ${item.value}`}
      </Tag>
    ));
  };

  return (
    <div style={{ paddingTop: 0 }}>
      <Row>
        <Col span={24}>
          <div className='flex min-h-18 items-start justify-between overflow-auto rounded-[0.3125rem] border-2 border-dashed border-[#0000007d] p-4 text-[#000000d9] max-sm:flex-wrap [&_p.hide]:hidden [&_p.show]:inline-block [&_p.show]:w-full [&_p.show]:self-center [&>p]:m-0 [&>p]:text-center'>
            <div className='mb-2 w-full md:mb-0'>
              <p className={clsx('m-0! w-full text-center', count > 0 ? 'hide' : 'show')}>
                Selecione suas opções de filtros acima
              </p>
              {getLabels()}
            </div>
            <div className={`block self-center whitespace-nowrap rounded-sm${count > 0 ? ' ' : 'hide'}`}>
              <Button icon={<CloseOutlined />} disabled={disabled} onClick={cleanFilter} type='text'>
                Limpar
              </Button>
            </div>
            {onApply && (
              <div className={`ml-2 block self-center rounded-sm ${count > 0 ? ' ' : 'hide'}`}>
                <Button
                  type='primary'
                  icon={<CheckOutlined />}
                  disabled={count === active || disabled}
                  onClick={handleApply}
                >
                  Aplicar
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Tags;
